import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const METHODS = ['digital_form', 'video', 'phone', 'in_person', 'post_exit_2_4w'];
export const STATUSES = ['pending', 'sent', 'completed', 'expired', 'cancelled'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useOdExitInterviewsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboarding-exit-interviews?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const markSent = useCallback(async (id) => {
    try { await api.post(`/v1/offboarding-exit-interviews/${id}/mark-sent`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try { await api.post(`/v1/offboarding-exit-interviews/${id}/cancel`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  // HR creates the interview record for a specific offboarding (one per process,
  // enforced backend-side → 409 od.exitInterview.alreadyExists). This is the only
  // create path; the record then either HR conducts or the leaver fills via
  // self-service. Errors surface through the Axios interceptor toast.
  const create = useCallback(async (formData) => {
    try {
      await api.post('/v1/offboarding-exit-interviews', {
        offboardingId: formData.offboardingId,
        method: formData.method,
        isAnonymous: !!formData.isAnonymous,
        isOptionalForTermination: !!formData.isOptionalForTermination,
      });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const objectSchema = useMemo(() => ([
    {
      name: 'offboardingId', label: t('offboardingExitInterview.form.offboarding'),
      type: 'asyncSelect', grid: 12, required: true,
      // Pick among live offboardings only — a closed/cancelled process shouldn't
      // get a new interview. Backend still blocks a duplicate per offboarding.
      onSearch: async (q) => {
        const params = q ? { 'personName[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/offboardings', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items
          .filter((o) => o.status !== 'completed' && o.status !== 'cancelled')
          .map((o) => ({ id: o.id, name: o.personFullName || o.id }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/offboardings/${id}`);
        const o = res.data?.data || {};
        return { id, name: o.personFullName || id };
      },
    },
    {
      name: 'method', label: t('offboardingExitInterview.form.method'),
      type: 'select', grid: 12, required: true,
      options: METHODS.map((m) => ({ id: m, name: t(`offboardingExitInterview.method.${m}`, { defaultValue: m }) })),
    },
    { name: 'isAnonymous', label: t('offboardingExitInterview.form.isAnonymous'), type: 'checkbox', grid: 6 },
    { name: 'isOptionalForTermination', label: t('offboardingExitInterview.form.isOptionalForTermination'), type: 'checkbox', grid: 6 },
  ]), [t]);

  const columns = useMemo(() => {
    const methodLabel = (s) => t(`offboardingExitInterview.method.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`offboardingExitInterview.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'offboardingId', label: 'OD', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('offboardingExitInterview.person'),
        options: {
          filter: false, sort: true,
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (row?.isAnonymous) return t('offboardingExitInterview.anonymousMarker');
            return v ? <PersonCell name={v} profilePicture={row?.personProfilePicture} /> : '–';
          },
        },
      },
      {
        name: 'method',
        label: t('offboardingExitInterview.methodLabel'),
        options: {
          ...multiSelectFilter(t('offboardingExitInterview.methodLabel'), METHODS, methodLabel),
          customBodyRender: (v) => methodLabel(v),
        },
      },
      {
        name: 'status',
        label: t('offboardingExitInterview.statusLabel'),
        options: {
          ...multiSelectFilter(t('offboardingExitInterview.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      numericFilterColumn('npsScore', t('offboardingExitInterview.npsScore'), { decimals: 0 }),
      {
        name: 'reasonCategory',
        label: t('offboardingExitInterview.reasonCategory'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'in' },
          customBodyRender: (v) => v || '–',
        },
      },
      {
        name: 'minNRespected',
        label: t('offboardingExitInterview.minNRespected'),
        options: {
          filter: true, sort: true,
          customBodyRender: (v) => v ? t('common.yes') : t('offboardingExitInterview.insufficientData'),
        },
      },
      {
        name: 'isAnonymous',
        label: t('offboardingExitInterview.isAnonymous'),
        options: {
          filter: true, sort: true,
          customBodyRender: (v) => v ? t('common.yes') : t('common.no'),
        },
      },
      dateFilterColumn('completedAt', t('offboardingExitInterview.completedAt'), lang),
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, objectSchema, refresh, create, markSent, cancel };
};

export default useOdExitInterviewsApi;
